import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Button,
  AriaLiveRegion,
} from '@resy/ui-kitchen';
import { CITY_FILTER } from '../../ExperientialEvents';
import './EventsSearchList.scss';

const KEY = {
  ENTER: 'Enter', SPACE: ' ', ARROW_DOWN: 'ArrowDown', ARROW_UP: 'ArrowUp', ESCAPE: 'Escape',
};

const EventsSearchList = (props) => {
  const {
    locations,
    onLocationSelect,
    typeOfFilter,
    onFilterSelect,
    selectedFilterValues,
    onClickFilterEvents,
  } = props;

  const selectedLocationName = typeOfFilter === CITY_FILTER ? selectedFilterValues.name : '';
  const [state, setState] = React.useState(null);
  const filteredLocations = locations?.filter((event) => !!event);

  const onListBlur = () => {
    setState(null);
  };

  const onItemFocus = (e) => {
    setState({ [e.target.id]: true });
  };

  const onItemKeyDown = (e, location) => {
    let elementToFocus;
    if (e.key === KEY.ARROW_DOWN) {
      if (e.currentTarget?.nextSibling) {
        elementToFocus = e.currentTarget?.nextSibling;
      } else if (e.currentTarget === e.currentTarget?.parentElement?.lastElementChild) {
        elementToFocus = e.currentTarget?.parentElement?.firstElementChild;
      }
    } else if (e.key === KEY.ARROW_UP) {
      if (e.currentTarget?.previousSibling) {
        elementToFocus = e.currentTarget?.previousSibling;
      } else if (e.currentTarget === e.currentTarget?.parentElement?.firstElementChild) {
        elementToFocus = e.currentTarget?.parentElement?.lastElementChild;
      }
    }

    if (elementToFocus) {
      elementToFocus.focus();
      return;
    }

    if (e.key === KEY.ESCAPE) {
      setState({});
      document.querySelector('#locationSearch')?.focus();
      return;
    }

    if (e.key === KEY.ENTER || e.key === KEY.SPACE) {
      onLocationSelect(location);
      e.preventDefault();
    }
  };

  const getAriaLiveRegionResultMessage = () => {
    if (typeOfFilter !== CITY_FILTER) {
      return null;
    }
    const numResults = filteredLocations?.length;
    const copyText = (numResults > 0) ? `There are ${numResults} results from your query. Use the up and down arrows to navigate results.`
      : 'No results.';
    return copyText;
  };

  const getIsActive = (locationName) => locationName?.toLowerCase() === selectedLocationName?.toLowerCase();

  const getIsFilterActive = (valueName, type) => {
    const dataSelected = selectedFilterValues?.filter((filterItem) => filterItem.type === type)[0]?.data;
    if (dataSelected.label === valueName) return true;
    return null;
  };

  const renderSearchCityResults = () => (
    <>
      <AriaLiveRegion>{getAriaLiveRegionResultMessage()}</AriaLiveRegion>
      <h2 className="EventsSearchList__header">
        Locations
      </h2>
      <ul
        role="listbox"
        id="cities-list"
        className="EventsSearchList__list allcities"
        onBlur={onListBlur}
        tabIndex="-1"
      >
        {filteredLocations?.map((location) => (
          <li
            role="option"
            aria-selected={state && state[location.id] ? state[location.id] : 'false'}
            tabIndex="-1"
            key={location.id}
            id={location.id}
            onFocus={onItemFocus}
            onKeyDown={(e) => onItemKeyDown(e, location)}
            onClick={() => onLocationSelect(location)}
            className={cx('EventsSearchList__item', {
              active: getIsActive(location.name),
            })}
          >
            {location.name}
          </li>
        ))}
      </ul>
    </>
  );

  const dataEvents = filteredLocations?.filter((labelItem) => labelItem.type === 'events')[0]?.data;
  const dataMonths = filteredLocations?.filter((labelItem) => labelItem.type === 'months')[0]?.data;

  const allMonthsButton = dataMonths && dataMonths.find((month) => month.value === '');
  const monthsList = dataMonths && dataMonths.filter((month) => month.value !== '');

  const renderMonthButton = (month) => (
    <Button
      variant="no-style"
      className={cx('EventsSearchList__anchor', {
        active: getIsFilterActive(month.label, 'months'),
      })}
      aria-current={getIsFilterActive(month.label, 'months')}
      onClick={() => {
        const selectedData = {
          type: 'months',
          data: month,
        };
        onFilterSelect(selectedData, 'months');
      }}
    >
      {month.label}
    </Button>
  );

  const renderFilterEvents = () => (
    <div className="EventsSearchList__wrapper">
      <div className="EventsSearchList__listContainer">
        <ul
          role="listbox"
          id="cities-list"
          className="EventsSearchList__list allevents"
          onBlur={onListBlur}
          tabIndex="-1"
        >
          {dataEvents?.map((eventItem) => (
            <li
              key={eventItem.value}
              className={`EventsSearchList__item EventsSearchList__item--status ${eventItem.value === 0 ? 'allEvents' : ''}`}
            >
              <Button
                variant="no-style"
                className={cx('EventsSearchList__anchor', {
                  active: getIsFilterActive(eventItem.label, 'events'),
                })}
                aria-current={getIsFilterActive(eventItem.label, 'events')}
                onClick={() => {
                  const selectedData = {
                    type: 'events',
                    data: eventItem,
                  };
                  onFilterSelect(selectedData, 'events');
                }}
              >
                {eventItem.label}
              </Button>
            </li>
          ))}
        </ul>
        <hr />
        <ul
          className="EventsSearchList__list allmonths"
          role="listbox"
          id="status-list"
          onBlur={onListBlur}
          tabIndex="-1"
        >
          <li className="EventsSearchList__item allMonths">
            { renderMonthButton(allMonthsButton) }
          </li>
          <li className="EventsSearchList__item">
            <ul
              className={cx('EventsSearchList__list', { twocols: monthsList.length > 6 })}
            >
              {monthsList.map((month) => (
                <li
                  key={month.key}
                  className="EventsSearchList__item"
                >
                  { renderMonthButton(month) }
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </div>
      <div className="EventsSearchList__footer">
        <Button
          className="EventsSearchList__filtercta"
          aria-label="Filter Events"
          onClick={onClickFilterEvents}
        >
          Filter Events
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className="EventsSearchList EventsSearchList__allData"
    >
      {typeOfFilter === CITY_FILTER ? (
        renderSearchCityResults()
      ) : (
        renderFilterEvents()
      )}
    </div>
  );
};

EventsSearchList.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  onLocationSelect: PropTypes.func,
  typeOfFilter: PropTypes.string.isRequired,
  onFilterSelect: PropTypes.func.isRequired,
  selectedFilterValues: PropTypes.oneOfType([
    PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
    PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string.isRequired,
      data: PropTypes.shape({}).isRequired,
    }).isRequired).isRequired,
  ]).isRequired,
  onClickFilterEvents: PropTypes.func.isRequired,
};

EventsSearchList.defaultProps = {
  locations: [],
  onLocationSelect: () => {},
};

EventsSearchList.displayName = 'EventsSearchList';
export default EventsSearchList;
